import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import { Card, Icon, IconType } from 'shared';
import { Button, LargeTitle, palettes, SmallBody, Tooltip, useFlashMessage } from '@indigo-ag/component-module';
import { RiskClassificationLevel } from '@indigo-ag/schema';
import { RiskLevelSelect } from './RiskLevelSelect';
import i18next from 'i18next';
import { ContactRiskClassificationItem } from '../../../phases/Phase1.utils';
import { useMutation } from '@apollo/client';
import {
  addContactClassificationLevel,
  getOpportunityContactRolesAndBackgroundCheckGroup
} from 'features/brazil-grower/queries/backgroundCheck.queries';
import { getInfoText, getInfoTextColor, getTooltipText } from './RiskClassificationCard.utils';
import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';

const CardContainer = styled.div`
  height: 100%;
  flex: 1;
  min-width: 300px;
  max-width: 480px;
`;

const CardContentContainer = styled.div`
  min-height: 74px;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 1rem;
`;

const InfoIconWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const InfoText = styled.span`
  color: ${palettes.brand.blue.base};
`;

interface RiskClassificationCardProps {
  riskClassification: ContactRiskClassificationItem;
  agFinancingId: string;
}

export const RiskClassificationCard = ({ riskClassification, agFinancingId }: RiskClassificationCardProps) => {
  const [riskLevel, setRiskLevel] = useState<RiskClassificationLevel | null>(
    riskClassification.riskClassificationLevel || null
  );

  const { agFinance } = useContext(FinanceProcessContext);

  const [addContactClassificationLevelFn, { loading }] = useMutation(addContactClassificationLevel);

  const { addFlashMessage } = useFlashMessage();

  const handleSave = async () => {
    if (!riskLevel || riskLevel === RiskClassificationLevel.NOT_CLASSIFIED) {
      addFlashMessage({
        content: i18next.t('riskClassificationLevelRequired'),
        title: '',
        variant: 'error'
      });
      return;
    }
    try {
      await addContactClassificationLevelFn({
        variables: {
          data: {
            contactTaxIdentifier: riskClassification.contactTaxIdentifier,
            riskClassificationLevel: riskLevel,
            agFinancingId
          }
        },
        refetchQueries: [
          {
            query: getOpportunityContactRolesAndBackgroundCheckGroup,
            variables: { opportunityId: agFinance?.orderInfo?.opportunities?.[0].id }
          }
        ],
        awaitRefetchQueries: true
      });
      addFlashMessage({
        content: i18next.t('riskClassificationLevelSaved'),
        title: '',
        variant: 'success'
      });
    } catch (error) {
      addFlashMessage({
        content: i18next.t('riskClassificationLevelErrorWhileSaving'),
        title: '',
        variant: 'error'
      });
    }
  };

  const isDisabled = riskClassification.isExpired !== null && !riskClassification.isExpired;

  return (
    <CardContainer>
      <Card
        title={
          <LargeTitle style={{ color: palettes.brand.blue.base, marginBottom: '2rem' }}>
            {i18next.t('preAnalysisRiskLevel-risk-classification')}
          </LargeTitle>
        }
      >
        <Tooltip
          title={
            <SmallBody style={{ color: getInfoTextColor(riskClassification) }}>
              {getTooltipText(riskClassification)}
            </SmallBody>
          }
          placement="top-start"
        >
          <InfoIconWrapper>
            <Icon iconType={IconType.Info} color={getInfoTextColor(riskClassification)} />
            <InfoText>{getInfoText(riskClassification)}</InfoText>
          </InfoIconWrapper>
        </Tooltip>

        <CardContentContainer>
          <RiskLevelSelect value={riskLevel} onChange={setRiskLevel} disabled={isDisabled} />
        </CardContentContainer>

        <ButtonsWrapper>
          <Button onClick={handleSave} size="small" variant="primary" busy={loading} disabled={loading || isDisabled}>
            {i18next.t('save')}
          </Button>
        </ButtonsWrapper>
      </Card>
    </CardContainer>
  );
};
